import {
  FormControl,
  Stack,
  TextField,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
  styled,
  Chip
} from '@mui/material';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { useLocation } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  initialState,
  reducerUpdateAdvanceClear,
  reducerUpdateAdvanceSearch,
  reducerUpdateAdvanceSearchIncludeKeywords
} from 'src/features/advanceSearchSocialMedia';
import AdvanceSearchSocialMedia from 'src/components/AdvanceSearchSocialMedia';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import TooltipCustomize from 'src/components/TooltipCustomize';
import RefreshIcon from '@mui/icons-material/Refresh';
import useCampaign from 'src/services/useCampaign';
import { fillParamAdvanceSearchEmpty } from 'src/utils/fillParamAdvanceSearchEmpth';
import { IGranularity } from 'src/models/general';
import changeObjectToQuery from 'src/utils/changeObjectToQuery';
import useQueryUrl from 'src/hooks/useQuery';
import SelectAutoRefreshInterval from 'src/components/SelectAutoRefreshInterval';
import SelectDateRangeRetail from 'src/components/Retail/SelectDateRangeRetail';
import { RestoreFromTrash } from '@mui/icons-material';

const ChipCustom = styled(Chip)(
  ({ theme }) => `
    background-color: ${theme.palette.primary.light};
    border-radius: ${theme.spacing(1)};
    padding: 10px ${theme.spacing(2)};
    height: 29px;
    width: 100px;
    .MuiChip-label {
        color: #0A789D;
        font-size: 14px;
        line-height: 130%;
        margin: 0 4px;
    }
    .MuiChip-deleteIcon {
        font-size: 14px;
        color: #0A789D;
    }
  `
);

function BottomHeader() {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const urlquery = useQueryUrl();
  const title = pathname.split('/')[2];
  const tabQuery = urlquery.get('tab');
  const sentimentQuery = urlquery.get('sentiment');

  const advanceSearch = useAppSelector(
    (state) => state.storeAdvanceSearchSocialMedia
  );

  const isStreamPreviewSentiment =
    tabQuery === 'stream_preview' && Boolean(sentimentQuery);

  const {
    streamsCommentingToPost,
    selectedOriginalToPost,
    searchOriginalToPost,
    ...rest
  } = advanceSearch;

  const query = changeObjectToQuery({
    ...rest,
    sentiment: isStreamPreviewSentiment ? [sentimentQuery] : rest.sentiment
  });

  const [localSearch, setLocalSearch] = useState<string>('');

  const handleClear = () => {
    setLocalSearch('');
    dispatch(reducerUpdateAdvanceClear(initialState));
  };

  const handleSearch = () => {
    dispatch(reducerUpdateAdvanceSearchIncludeKeywords([localSearch]));
    setLocalSearch('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') handleSearch();
  };

  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState<boolean>(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const { getAllMetricCampaign } = useCampaign();

  const projectId = useAppSelector((state) => state.users.project.id);

  const advanceSearchSocialMedia = useAppSelector(
    (state) => state.storeAdvanceSearchSocialMedia
  );

  const { dateRangeFormatted, objectIdList } = useAppSelector(
    (state) => state.analyticArguments
  );

  const objectList = useAppSelector(
    (state) => state.objectManagement.objectList.data
  );

  const fillAdvanceSearchSocialMedia = useMemo(() => {
    return fillParamAdvanceSearchEmpty(advanceSearchSocialMedia);
  }, [advanceSearchSocialMedia]);

  const objectListByObjectTypeActive = useMemo(() => {
    const dataList = objectList.filter((object) =>
      objectIdList.find((id) => id === object.id)
    );
    return dataList.map((object) => {
      return object.id;
    });
  }, [objectIdList]);

  const granularity: IGranularity = {
    unit: 'day',
    value: 1
  };
  const timezone = useAppSelector((state) => state.users.project.timezone);

  const finalPayload = {
    ...fillAdvanceSearchSocialMedia,
    projectId,
    ...dateRangeFormatted,
    objectList: objectListByObjectTypeActive,
    granularity,
    timezone
  };

  const handleRefresh = () => {
    getAllMetricCampaign(finalPayload);
  };

  const advanceSearchKeys = query
    ? query.split(';').slice(0, query.split(';').length - 1)
    : [];

  const handleDeleteFilter = (item: string) => {
    const splitFilter = item.split(':');
    const deletedFilter = splitFilter[0].trim();
    dispatch(
      reducerUpdateAdvanceSearch({
        ...advanceSearch,
        [deletedFilter]: initialState[deletedFilter]
      })
    );
  };

  const handleDeleteAllFilter = () => {
    dispatch(reducerUpdateAdvanceSearch(initialState));
  };

  // auto refresh metric
  const { selectedAutoRefreshInterval } = useAppSelector(
    (store) => store.autoRefreshMetric
  );

  const FilterSection = () => {
    return (
      <>
        <Stack
          direction="row"
          overflow="auto"
          maxWidth={300}
          spacing={1}
          sx={{
            scrollbarWidth: 'thin'
          }}
        >
          {advanceSearchKeys
            ? advanceSearchKeys.map((item) => {
                return (
                  <Tooltip title={item}>
                    <ChipCustom
                      variant="filled"
                      deleteIcon={<ClearIcon />}
                      onDelete={() => handleDeleteFilter(item)}
                      label={item}
                      size="small"
                    />
                  </Tooltip>
                );
              })
            : null}
        </Stack>
        {advanceSearchKeys.length > 0 ? (
          <Stack
            direction="row"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={handleDeleteAllFilter}
          >
            <RestoreFromTrash fontSize="small" />
            <Typography fontSize="14px">Clear Filters</Typography>
          </Stack>
        ) : null}
      </>
    );
  };

  useEffect(() => {
    if (selectedAutoRefreshInterval === 0) return;
    const interval = setInterval(() => {
      handleRefresh();
    }, selectedAutoRefreshInterval);

    return () => {
      clearInterval(interval);
    };
  }, [selectedAutoRefreshInterval]);

  return (
    <Stack direction="column" my={2}>
      <Stack
        direction={isLargeScreen ? 'row' : 'column'}
        justifyContent={isLargeScreen ? 'space-between' : 'unset'}
        gap={1}
      >
        <Stack
          direction={matchesUpMd ? 'row' : 'column'}
          spacing={1}
          alignItems="center"
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            className="filtering"
            width="100%"
            maxWidth={isLargeScreen ? '400px' : 'unset'}
          >
            <TextField
              size="small"
              InputProps={{
                startAdornment: (
                  <IconButton size="small" onClick={handleSearch}>
                    <SearchTwoToneIcon />
                  </IconButton>
                ),
                sx: { borderRadius: '16px', backgroundColor: 'white' },
                endAdornment: localSearch ? (
                  <IconButton size="small" onClick={handleClear}>
                    <ClearIcon />
                  </IconButton>
                ) : null
              }}
              placeholder={`Search ${title}`}
              onChange={(e) => setLocalSearch(e.target.value)}
              value={localSearch}
              onKeyDown={handleKeyDown}
              fullWidth
            />
            <SelectDateRangeRetail
              selectProps={{
                sx: {
                  borderRadius: '16px',
                  width: '200px'
                }
              }}
            />
          </Stack>
          <Stack
            direction="row"
            justifyContent={isLargeScreen ? 'unset' : 'space-between'}
            width={matchesUpMd ? 'unset' : '100%'}
            spacing={1}
            className="timeframeAndRefresh"
            alignItems="center"
          >
            <FormControl size="small" fullWidth={matchesUpMd ? false : true}>
              <TooltipCustomize title="Advance search" placement="top-start">
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: '16px',
                    height: theme.spacing(5),
                    minWidth: theme.spacing(6),
                    background: theme.palette.primary.main
                  }}
                  onClick={handleClickOpen}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FilterListIcon htmlColor="#FFFFFF" />
                    <Typography color="white" fontWeight={700}>
                      Filter
                    </Typography>
                  </Stack>
                </Button>
              </TooltipCustomize>
              {open && (
                <AdvanceSearchSocialMedia open={open} onClose={handleClose} />
              )}
            </FormControl>
            {matchesUpMd ? <FilterSection /> : null}
          </Stack>
        </Stack>
        {!matchesUpMd ? <FilterSection /> : null}
        <Stack
          direction="row"
          justifyContent={isLargeScreen ? 'unset' : 'space-between'}
          spacing={2}
        >
          <Stack direction="row" gap={1}>
            <TooltipCustomize title="Refresh all metric" placement="top-start">
              <Button
                variant="contained"
                onClick={handleRefresh}
                sx={{
                  borderRadius: '6px',
                  height: theme.spacing(5),
                  minWidth: theme.spacing(6),
                  background: theme.palette.primary.main
                }}
              >
                <RefreshIcon htmlColor={theme.colors.primary.lighter} />
              </Button>
            </TooltipCustomize>
            <FormControl size="small" sx={{ width: 100 }}>
              <SelectAutoRefreshInterval />
            </FormControl>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default BottomHeader;
